import { showToast } from "../../utils/toast";
import { JsonResult, sendJson } from "./request";
import { openTaskModal } from "./tasks_export";
import { subscribeCsClientActivity } from "../../channels/cs_client_activity_channel";

const PANES = ["campaigns", "users", "notes", "activity", "tasks"];
const page = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-client-page]");
const customerId = (): string => page()?.dataset.id || "";

function currentPane(): string {
  const name = window.location.hash.replace("#", "");
  return PANES.includes(name) ? name : "campaigns";
}

async function loadPane(name: string, force = false): Promise<void> {
  const pane = document.querySelector<HTMLElement>(`[data-cs-pane="${name}"]`);
  if (!pane || (pane.dataset.loaded && !force)) return;
  pane.dataset.loaded = String(Date.now());
  try {
    const response = await fetch(pane.dataset.url!, { headers: { Accept: "text/html" } });
    pane.innerHTML = response.ok ? await response.text() : '<div class="empty">Couldn\'t load this tab.</div>';
  } catch (_e) {
    pane.innerHTML = '<div class="empty">Couldn\'t load this tab.</div>';
  }
  const count = pane.querySelector<HTMLElement>("[data-cs-count]")?.dataset.csCount;
  const badge = document.querySelector<HTMLElement>(`[data-cs-pane-link="${name}"] [data-cs-pane-count]`);
  if (badge && count !== undefined) badge.textContent = count;
  (window as any).mountLogos?.();
}

async function loadHubspotCounts(): Promise<void> {
  const root = page();
  if (!root || root.dataset.countsLoaded) return;
  root.dataset.countsLoaded = "1";
  try {
    const response = await fetch(`/customer_success/customers/${customerId()}/hubspot_counts`, { headers: { Accept: "application/json" } });
    if (!response.ok) return;
    const counts: Record<string, number | null> = await response.json();
    Object.entries(counts).forEach(([name, count]) => {
      const pane = document.querySelector<HTMLElement>(`[data-cs-pane="${name}"]`);
      const badge = document.querySelector<HTMLElement>(`[data-cs-pane-link="${name}"] [data-cs-pane-count]`);
      if (badge && count !== null && !pane?.querySelector("[data-cs-count]")) badge.textContent = String(count);
    });
  } catch (_e) {
    return;
  }
}

function showPane(): void {
  subscribeActivity();
  if (!page()) return;
  loadHubspotCounts();
  const name = currentPane();
  document.querySelectorAll<HTMLElement>("[data-cs-pane]").forEach((pane) => (pane.hidden = pane.dataset.csPane !== name));
  document.querySelectorAll<HTMLElement>("[data-cs-pane-link]").forEach((link) => link.classList.toggle("active", link.dataset.csPaneLink === name));
  if (name === "activity" && document.querySelector<HTMLElement>('[data-cs-pane="activity"]')?.dataset.loaded) refreshActivity();
  loadPane(name);
}

function closeUserDrawer(): void {
  document.querySelectorAll<HTMLElement>("[data-cs-user-drawer], [data-cs-user-backdrop]").forEach((el) => (el.hidden = true));
}

async function openUserDrawer(url: string): Promise<void> {
  const drawer = document.querySelector<HTMLElement>("[data-cs-user-drawer]");
  if (!drawer) return;
  const response = await fetch(url, { headers: { Accept: "text/html" } });
  if (!response.ok) return showToast("Couldn't load the user");
  drawer.innerHTML = await response.text();
  document.querySelectorAll<HTMLElement>("[data-cs-user-drawer], [data-cs-user-backdrop]").forEach((el) => (el.hidden = false));
}

const PAGE_SIZE = 10;

function syncLessButton(section: HTMLElement): void {
  const extra = section.querySelectorAll(".act-item").length > PAGE_SIZE;
  let less = section.querySelector<HTMLButtonElement>("[data-cs-user-less]");
  if (extra && !less) {
    less = document.createElement("button");
    less.type = "button";
    less.className = "btn btn-sm cs-load-more cs-show-less";
    less.dataset.csUserLess = "";
    less.textContent = "Show less";
    section.append(less);
  }
  if (!extra) less?.remove();
}

async function loadMore(button: HTMLButtonElement): Promise<void> {
  const section = button.closest<HTMLElement>(".dsec")!;
  if (!section.dataset.firstMore) section.dataset.firstMore = button.dataset.csUserMore;
  button.disabled = true;
  button.textContent = "Loading…";
  const response = await fetch(button.dataset.csUserMore!, { headers: { Accept: "text/html" } });
  if (!response.ok) {
    button.disabled = false;
    button.textContent = "Load more";
    return showToast("Couldn't load more");
  }
  button.outerHTML = await response.text();
  section.querySelector("[data-cs-user-less]")?.remove();
  syncLessButton(section);
}

function showLess(section: HTMLElement): void {
  section.querySelectorAll(".act-item").forEach((item, index) => index >= PAGE_SIZE && item.remove());
  section.querySelector("[data-cs-user-more]")?.remove();
  section.querySelector("[data-cs-user-less]")?.remove();
  const more = document.createElement("button");
  more.type = "button";
  more.className = "btn btn-sm cs-load-more";
  more.dataset.csUserMore = section.dataset.firstMore;
  more.textContent = "Load more";
  section.append(more);
  section.querySelector(".dsec-head")?.scrollIntoView({ block: "nearest" });
}

async function toggleCampaign(row: HTMLElement): Promise<void> {
  const detail = row.nextElementSibling as HTMLElement | null;
  if (!detail?.classList.contains("reply-row")) return;
  const open = !detail.classList.contains("open");
  detail.classList.toggle("open", open);
  row.classList.toggle("open", open);
  row.setAttribute("aria-expanded", String(open));
  const cell = detail.querySelector<HTMLElement>("[data-cs-camp-detail]");
  if (!open || !cell || cell.dataset.loaded) return;
  cell.dataset.loaded = "1";
  try {
    const response = await fetch(row.dataset.statsUrl!, { headers: { Accept: "text/html" } });
    if (!response.ok) throw new Error(String(response.status));
    cell.innerHTML = await response.text();
  } catch (_e) {
    delete cell.dataset.loaded;
    cell.innerHTML = '<span class="muted">Couldn\'t load the campaign summary.</span>';
  }
}

async function noteRequest(url: string, method: string, body: FormData, message: string): Promise<JsonResult | null> {
  const result = await sendJson(url, method, body, false);
  showToast(result.ok ? message : "Couldn't save the note");
  return result.ok ? result : null;
}

function bumpNoteCount(by: number): void {
  const pane = document.querySelector<HTMLElement>('[data-cs-pane="notes"] [data-cs-count]');
  if (!pane) return;
  pane.dataset.csCount = String(Math.max(0, Number(pane.dataset.csCount || 0) + by));
  const badge = document.querySelector<HTMLElement>('[data-cs-pane-link="notes"] [data-cs-pane-count]');
  if (badge) badge.textContent = pane.dataset.csCount;
  const list = document.querySelector<HTMLElement>('[data-cs-pane="notes"] .notes-list');
  if (list && !list.querySelector("[data-cs-note]") && !list.querySelector(".empty")) list.innerHTML = '<div class="empty">No notes yet.</div>';
}

function saveNote(note: HTMLElement): Promise<void> {
  const body = new FormData();
  body.set("id", note.dataset.id || "");
  body.set("timestamp", note.dataset.timestamp || "");
  const text = note.querySelector<HTMLTextAreaElement>(".edit-box textarea")!.value;
  body.set("notes", text);
  return noteRequest("/customer_success/update_note", "PATCH", body, "Note updated").then((result) => {
    if (!result) return;
    note.querySelector<HTMLElement>(".note-body")!.textContent = text.trim();
    note.classList.remove("editing");
  });
}

function deleteNote(note: HTMLElement): Promise<void> {
  const body = new FormData();
  body.set("note[id]", note.dataset.id || "");
  body.set("note[customer_id]", customerId());
  return noteRequest("/customer_success/archive_note", "DELETE", body, "Note deleted").then((result) => {
    if (!result) return;
    note.remove();
    bumpNoteCount(-1);
  });
}

function filterActivity(button: HTMLElement): void {
  const source = button.dataset.src || "all";
  button.parentElement!.querySelectorAll("button").forEach((b) => b.classList.toggle("active", b === button));
  document.querySelectorAll<HTMLElement>("[data-cs-activity]").forEach((item) => (item.hidden = source !== "all" && item.dataset.csActivity !== source));
}

const ACTIVITY_REFRESH_MS = 30_000;
let activityRefreshing = false;

let activitySubscription: { id: string; unsubscribe: () => void } | null = null;

function subscribeActivity(): void {
  const id = page() ? customerId() : "";
  if (activitySubscription?.id === id) return;
  activitySubscription?.unsubscribe();
  activitySubscription = null;
  if (!id) return;
  const subscription = subscribeCsClientActivity(id, () => {
    refreshActivity(true);
    window.setTimeout(() => refreshActivity(true), 5000);
  });
  activitySubscription = { id, unsubscribe: () => subscription.unsubscribe() };
}

async function refreshActivity(force = false): Promise<void> {
  const pane = document.querySelector<HTMLElement>('[data-cs-pane="activity"]');
  if (!page() || !pane?.dataset.loaded || activityRefreshing) return;
  if (!force && (pane.hidden || document.visibilityState !== "visible" || Date.now() - Number(pane.dataset.loaded) < 3000)) return;
  if (Array.from(pane.querySelectorAll("audio")).some((audio) => !audio.paused)) return;
  activityRefreshing = true;
  try {
    const response = await fetch(pane.dataset.url!, { headers: { Accept: "text/html" } });
    if (!response.ok) return;
    const items = () => Array.from(pane.querySelectorAll<HTMLElement>("[data-cs-activity]"));
    const known = new Set(items().map((item) => item.dataset.id));
    const open = new Set(items().filter((item) => item.classList.contains("open")).map((item) => item.dataset.id));
    const filter = pane.querySelector<HTMLElement>("[data-cs-activity-filter] button.active")?.dataset.src;
    pane.innerHTML = await response.text();
    pane.dataset.loaded = String(Date.now());
    const fresh = items().filter((item) => !known.has(item.dataset.id));
    items().forEach((item) => {
      item.classList.toggle("open", open.has(item.dataset.id));
      if (fresh.includes(item)) item.classList.add("tl-new");
    });
    const button = pane.querySelector<HTMLElement>(`[data-cs-activity-filter] [data-src="${filter || "all"}"]`);
    if (button) filterActivity(button);
    const count = pane.querySelector<HTMLElement>("[data-cs-count]")?.dataset.csCount;
    const badge = document.querySelector<HTMLElement>('[data-cs-pane-link="activity"] [data-cs-pane-count]');
    if (badge && count !== undefined) badge.textContent = count;
    if (fresh.length) showToast(fresh.length === 1 ? "1 new activity" : `${fresh.length} new activities`);
  } catch (_e) {
    return;
  } finally {
    activityRefreshing = false;
  }
}

async function tickTask(task: HTMLElement): Promise<void> {
  const done = task.dataset.status === "COMPLETED";
  const body = new FormData();
  body.set("id", task.dataset.id || "");
  body.set("status", done ? "NOT_STARTED" : "COMPLETED");
  const { ok } = await sendJson("/customer_success/change_status_stask", "PATCH", body, false);
  showToast(ok ? (done ? "Task reopened" : "Task completed") : "Couldn't update the task");
  if (ok) loadPane("tasks", true);
}

function handleClick(e: MouseEvent): void {
  if (!page()) return;
  const target = e.target as HTMLElement;
  const note = target.closest<HTMLElement>("[data-cs-note]");
  const task = target.closest<HTMLElement>("[data-cs-task]");

  const tab = target.closest<HTMLElement>("[data-cs-pane-link]");
  if (tab) {
    e.preventDefault();
    history.replaceState(history.state, "", `#${tab.dataset.csPaneLink}`);
    return showPane();
  }
  if (target.closest("[data-cs-add-note]")) {
    history.replaceState(history.state, "", "#notes");
    showPane();
    loadPane("notes").then(() => document.querySelector<HTMLTextAreaElement>("#cs-note-input")?.focus());
    return;
  }
  const userTask = target.closest<HTMLElement>("[data-cs-user-task]");
  if (userTask) {
    closeUserDrawer();
    return openTaskModal({ title: userTask.dataset.csUserTask });
  }
  const more = target.closest<HTMLButtonElement>("[data-cs-user-more]");
  if (more) return void loadMore(more);
  const sectionToggle = target.closest<HTMLElement>("[data-cs-dsec-toggle]");
  if (sectionToggle) {
    const collapsed = sectionToggle.closest(".dsec")!.classList.toggle("collapsed");
    sectionToggle.setAttribute("aria-expanded", String(!collapsed));
    return;
  }
  const less = target.closest<HTMLElement>("[data-cs-user-less]");
  if (less) return showLess(less.closest<HTMLElement>(".dsec")!);
  if (target.closest("[data-cs-user-close]") || target.closest("[data-cs-user-backdrop]")) return closeUserDrawer();

  const userRow = target.closest<HTMLElement>("[data-cs-user-url]");
  if (userRow && !target.closest("a")) {
    openUserDrawer(userRow.dataset.csUserUrl!);
    return;
  }
  const campRow = target.closest<HTMLElement>("[data-cs-camp-row]");
  if (campRow && !target.closest("a")) {
    toggleCampaign(campRow);
    return;
  }

  if (note && target.closest("[data-cs-note-more]")) {
    const open = note.classList.toggle("expanded");
    target.closest("[data-cs-note-more]")!.textContent = open ? "Show less" : "Show more";
    return;
  }
  if (note && target.closest("[data-cs-note-edit]")) return void note.classList.add("editing");
  if (note && target.closest("[data-cs-note-cancel]")) return void note.classList.remove("editing");
  if (note && target.closest("[data-cs-note-save]")) return void saveNote(note);
  if (note && target.closest("[data-cs-note-delete]")) return void (note.querySelector<HTMLElement>("[data-cs-note-confirm]")!.hidden = false);
  if (note && target.closest("[data-cs-note-delete-no]")) return void (note.querySelector<HTMLElement>("[data-cs-note-confirm]")!.hidden = true);
  if (note && target.closest("[data-cs-note-delete-yes]")) return void deleteNote(note);

  const filter = target.closest<HTMLElement>("[data-cs-activity-filter] button");
  if (filter) return filterActivity(filter);
  const toggle = target.closest<HTMLElement>("[data-cs-tl-toggle]");
  if (toggle) return void toggle.closest(".tl-item")?.classList.toggle("open");

  if (task && target.closest("[data-cs-task-tick]")) return void tickTask(task);
  if (task && target.closest("[data-cs-task-edit]")) return openTaskModal(JSON.parse(task.dataset.task || "{}"));
}

async function submitNote(form: HTMLFormElement): Promise<void> {
  const body = new FormData(form);
  body.set("customer_id", customerId());
  body.set("view", "client");
  const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
  if (button) button.disabled = true;
  const result = await noteRequest("/customer_success/create_note", "POST", body, "Note added");
  if (button) button.disabled = false;
  const list = document.querySelector<HTMLElement>('[data-cs-pane="notes"] .notes-list');
  if (!result || !list) return;
  list.querySelector(".empty")?.remove();
  list.insertAdjacentHTML("afterbegin", result.data.note_html || "");
  form.reset();
  bumpNoteCount(1);
}

let bound = false;

export function bindCsClientPage(): void {
  if (bound) return;
  bound = true;

  document.addEventListener("turbo:load", showPane);
  window.addEventListener("hashchange", showPane);
  document.addEventListener("click", handleClick);
  document.addEventListener("cs:task-saved", () => page() && loadPane("tasks", true));
  document.addEventListener("visibilitychange", () => refreshActivity());
  window.addEventListener("focus", () => refreshActivity());
  window.setInterval(() => refreshActivity(), ACTIVITY_REFRESH_MS);
  document.addEventListener("submit", (e) => {
    const form = e.target as HTMLFormElement;
    if (!page() || !form.matches("[data-cs-note-form]")) return;
    e.preventDefault();
    submitNote(form);
  });
  document.addEventListener("keydown", (e) => {
    const field = e.target as HTMLElement;
    if (e.key === "Escape") closeUserDrawer();
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && field.matches("[data-cs-note-form] textarea")) field.closest("form")!.requestSubmit();
  });
  showPane();
}
